import { createSlice, createAction } from '@reduxjs/toolkit';
import { globalReset } from '@/store/actions';
import { PhonemeProfile, PhonemeSkillMap, PaginatedResponse } from '@/services/speaking/types';

// === PHONEMES ACTIONS ===
export const fetchPhonemesRequest = createAction<any>('phonemes/fetchPhonemesRequest');
export const fetchPhonemesSuccess = createAction<PaginatedResponse<PhonemeProfile>>('phonemes/fetchPhonemesSuccess');
export const fetchPhonemesFailure = createAction<string>('phonemes/fetchPhonemesFailure');

export const fetchPhonemeByIdRequest = createAction<string>('phonemes/fetchPhonemeByIdRequest');
export const fetchPhonemeByIdSuccess = createAction<PhonemeProfile>('phonemes/fetchPhonemeByIdSuccess');
export const fetchPhonemeByIdFailure = createAction<string>('phonemes/fetchPhonemeByIdFailure');

export const fetchPhonemeSkillMapRequest = createAction('phonemes/fetchPhonemeSkillMapRequest');
export const fetchPhonemeSkillMapSuccess = createAction<PhonemeSkillMap>('phonemes/fetchPhonemeSkillMapSuccess');
export const fetchPhonemeSkillMapFailure = createAction<string>('phonemes/fetchPhonemeSkillMapFailure');

export const setPhonemeFilters = createAction<any>('phonemes/setPhonemeFilters');
export const clearPhonemeFilters = createAction('phonemes/clearPhonemeFilters');

// === STATE INTERFACE ===
export interface PhonemesState {
    phonemes: PhonemeProfile[];
    currentPhoneme: PhonemeProfile | null;
    skillMap: PhonemeSkillMap | null;
    phonemeSkillMap: PhonemeSkillMap | null;

    // Pagination
    totalPhonemes: number;
    currentPage: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;

    // Filters
    filters: {
        category?: string;
        place_of_articulation?: string;
        symbol?: string;
        page?: number;
        page_size?: number;
    };

    // Loading States
    isLoadingPhonemes: boolean;
    isLoadingPhoneme: boolean;
    isLoadingSkillMap: boolean;

    // Error States
    phonemesError: string | null;
    phonemeError: string | null;
    skillMapError: string | null;
}

const initialState: PhonemesState = {
    phonemes: [],
    currentPhoneme: null,
    skillMap: null,
    phonemeSkillMap: null,

    totalPhonemes: 0,
    currentPage: 1,
    hasNextPage: false,
    hasPreviousPage: false,

    filters: {},

    isLoadingPhonemes: false,
    isLoadingPhoneme: false,
    isLoadingSkillMap: false,

    phonemesError: null,
    phonemeError: null,
    skillMapError: null,
};

const phonemesSlice = createSlice({
    name: 'phonemes',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(globalReset, () => initialState)

            // === FETCH PHONEMES ===
            .addCase(fetchPhonemesRequest, (state, action) => {
                state.isLoadingPhonemes = true;
                state.phonemesError = null;
                if (action.payload) {
                    state.filters = action.payload;
                    state.currentPage = action.payload.page || 1;
                }
            })
            .addCase(fetchPhonemesSuccess, (state, action) => {
                state.isLoadingPhonemes = false;
                state.phonemes = action.payload.results;
                state.totalPhonemes = action.payload.count;
                state.hasNextPage = !!action.payload.next;
                state.hasPreviousPage = !!action.payload.previous;
            })
            .addCase(fetchPhonemesFailure, (state, action) => {
                state.isLoadingPhonemes = false;
                state.phonemesError = action.payload;
            })

            // === FETCH PHONEME BY ID ===
            .addCase(fetchPhonemeByIdRequest, (state) => {
                state.isLoadingPhoneme = true;
                state.phonemeError = null;
            })
            .addCase(fetchPhonemeByIdSuccess, (state, action) => {
                state.isLoadingPhoneme = false;
                state.currentPhoneme = action.payload;
            })
            .addCase(fetchPhonemeByIdFailure, (state, action) => {
                state.isLoadingPhoneme = false;
                state.phonemeError = action.payload;
            })

            // === FETCH PHONEME SKILL MAP ===
            .addCase(fetchPhonemeSkillMapRequest, (state) => {
                state.isLoadingSkillMap = true;
                state.skillMapError = null;
            })
            .addCase(fetchPhonemeSkillMapSuccess, (state, action) => {
                state.isLoadingSkillMap = false;
                state.skillMap = action.payload;
                state.phonemeSkillMap = action.payload;
            })
            .addCase(fetchPhonemeSkillMapFailure, (state, action) => {
                state.isLoadingSkillMap = false;
                state.skillMapError = action.payload;
            })

            // === FILTERS ===
            .addCase(setPhonemeFilters, (state, action) => {
                state.filters = action.payload;
                state.currentPage = 1;
            })
            .addCase(clearPhonemeFilters, (state) => {
                state.filters = {};
                state.currentPage = 1;
            });
    },
});

export default phonemesSlice.reducer;
